<template>
  <Modal
    :id="modalId"
    modal-class="small"
    hide-close-btn
    @before-open="reset()"
    @hidden="onHidden()"
  >
    <template #title>
      {{ titre }}
    </template>
    <template #subtitle>
      <p class="text-regular">
        {{ sousTitre }}
      </p>
    </template>
    <template #content>
      <!-- Parcours guidé, étape 1 : établissement situé en France ? -->
      <div
        v-if="isParcoursGuide && etape === 'france'"
        class="choix-parcours"
      >
        <button
          type="button"
          class="choix-parcours-carte"
          @click="repondreFrance(true)"
        >
          <span class="s6 bold">{{ $t("general.oui") }}</span>
        </button>
        <button
          type="button"
          class="choix-parcours-carte"
          @click="repondreFrance(false)"
        >
          <span class="s6 bold">{{ $t("general.non") }}</span>
        </button>
      </div>

      <!-- Parcours guidé, étape 3 : demande de compte ou de devis -->
      <div
        v-else-if="isParcoursGuide && etape === 'besoin'"
        class="choix-parcours choix-parcours--besoin"
      >
        <button
          type="button"
          class="choix-parcours-carte"
          :disabled="isLoading"
          @click="terminerParcours(BESOIN_COMPTE)"
        >
          <span class="s6 bold">{{ $t("catalogue-public.besoin-compte") }}</span>
          <span class="text-small">{{ $t("catalogue-public.besoin-compte-description") }}</span>
        </button>
        <button
          type="button"
          class="choix-parcours-carte"
          :disabled="isLoading"
          @click="terminerParcours(BESOIN_DEVIS)"
        >
          <span class="s6 bold">{{ $t("catalogue-public.besoin-devis") }}</span>
          <span class="text-small">{{ $t("catalogue-public.besoin-devis-description") }}</span>
        </button>
        <button
          type="button"
          class="choix-parcours-carte"
          :disabled="isLoading"
          @click="seConnecter()"
        >
          <span class="s6 bold">{{ $t("action.se-connecter") }}</span>
          <span class="text-small">{{ $t("connexion.jai-un-compte-desc") }}</span>
        </button>
      </div>

      <!-- Parcours guidé, étape 2 (hors France) ou changement de pays depuis le header -->
      <div
        v-else-if="!isPaysAutre"
        class="modal-choix-pays-content"
      >
        <SelectClassic
          v-model="selectedPaysOption"
          :label="$t('catalogue-public.champ-pays')"
          :inline="false"
          status="required"
          :placeholder="$t('general.placeholder-selectionner')"
          :options="paysOptions"
        >
          <template #label-icon>
            <UilGlobe size="16" />
          </template>
        </SelectClassic>

        <Toast
          v-if="isPaysModifie && linesInPanier > 0"
          variant="warning"
          :title="$t('catalogue-public.modal-changer-pays-panier')"
          show-icon
          hide-close-btn
        />
      </div>
    </template>
    <template #footer>
      <InfiniteLoader v-if="isLoading" />

      <template v-else-if="isParcoursGuide">
        <ButtonClassic
          v-if="etape !== 'france' || forceChoix"
          variant="ghost"
          color="secondary"
          icon="left"
          :label="$t('action.retour')"
          @click="retour()"
        >
          <template #left-icon>
            <UilArrowLeft />
          </template>
        </ButtonClassic>
        <ButtonClassic
          v-if="etape === 'pays'"
          variant="solid"
          color="primary"
          :label="$t('action.continuer')"
          :disabled="!selectedPays"
          @click="validerPaysExport()"
        />
      </template>

      <template v-else-if="isPaysAutre">
        <ButtonClassic
          variant="ghost"
          color="secondary"
          icon="left"
          :label="$t('catalogue-public.changer-de-pays')"
          @click="changerDePays()"
        >
          <template #left-icon>
            <UilGlobe />
          </template>
        </ButtonClassic>
        <ButtonClassic
          variant="solid"
          color="primary"
          icon="left"
          :label="$t('connexion.contacter-par-courriel')"
          @click="contacter()"
        >
          <template #left-icon>
            <UilEnvelopeAlt />
          </template>
        </ButtonClassic>
      </template>

      <template v-else>
        <ButtonClassic
          variant="ghost"
          color="secondary"
          :label="$t('action.annuler')"
          @click="$modal.hide(modalId)"
        />
        <ButtonClassic
          variant="solid"
          color="primary"
          :label="$t('action.valider')"
          :disabled="!selectedPays"
          @click="validerPays()"
        />
      </template>
    </template>
  </Modal>
</template>

<script>
import { mapGetters } from "vuex";

import {
  ButtonClassic,
  InfiniteLoader,
  Modal,
  SelectClassic,
  Toast,
} from "@lde/core_lde_vue";
import { UilArrowLeft, UilEnvelopeAlt, UilGlobe } from "@lde/vue-unicons";

import config from "@/config";
import {
  PAYS_AUTRE,
  PAYS_FRANCE,
  getMailtoPaysAutre,
  getPaysOptions,
} from "@/modules/cataloguePublic";

const BESOIN_COMPTE = "compte";
const BESOIN_DEVIS = "devis";

/**
 * Query param indiquant, après rechargement sur le catalogue d'un autre pays, qu'il faut ouvrir la demande de compte.
 */
const QUERY_CREATION_COMPTE = "creation_compte";

/**
 * Onglet de la modale de connexion où commence la demande de compte (choix du profil).
 */
const ONGLET_CHOIX_PROFIL = 1;

/**
 * Modale d'arrivée sur le catalogue public et de choix du pays.
 * - À l'arrivée, elle s'ouvre automatiquement et guide le visiteur :
 *   établissement en France ? (sinon choix du pays), puis demande de compte ou de devis.
 *   « Créer un compte » ouvre directement la demande de compte, « Faire un devis » laisse naviguer sur le catalogue.
 *   Si le visiteur la ferme sans aller au bout, le pays déjà indiqué (la France par défaut) est enregistré.
 * - Pour le pays "Autre", elle invite le visiteur à nous contacter et ne peut pas être fermée (pas de catalogue).
 * - Ensuite, elle permet de changer de pays depuis le header.
 */
export default {
  name: "ModalChoixPaysCataloguePublic",
  components: {
    ButtonClassic,
    InfiniteLoader,
    Modal,
    SelectClassic,
    Toast,
    UilArrowLeft,
    UilEnvelopeAlt,
    UilGlobe,
  },
  data() {
    return {
      BESOIN_COMPTE,
      BESOIN_DEVIS,
      modalId: "modal_choix_pays_catalogue_public",
      selectedPays: null,
      isLoading: false,
      /**
       * Étape du parcours guidé : "france", "pays" (hors France) ou "besoin" (compte ou devis).
       */
      etape: "france",
      /**
       * Pays indiqué pendant le parcours guidé, enregistré à la fin du parcours.
       */
      paysEnCours: null,
      /**
       * Relance le parcours guidé depuis le message de contact du pays "Autre".
       */
      forceChoix: false,
    };
  },
  computed: {
    ...mapGetters([
      "cataloguePublicPays",
      "cataloguePublicSlug",
      "isChoixPaysCataloguePublicRequis",
      "panier",
    ]),
    /**
     * Liste des pays : uniquement les pays hors France pendant le parcours guidé.
     */
    paysOptions() {
      const options = getPaysOptions(this.$t);
      return this.isParcoursGuide ? options.filter((option) => option.value !== PAYS_FRANCE) : options;
    },
    selectedPaysOption: {
      get() {
        return this.paysOptions.find((option) => option.value === this.selectedPays) || null;
      },
      set(option) {
        this.selectedPays = option?.value || null;
      },
    },
    /**
     * Le visiteur n'a pas encore choisi son pays.
     */
    isChoixInitial() {
      return !this.cataloguePublicPays;
    },
    /**
     * Parcours guidé : à l'arrivée, ou pour changer de pays depuis le message du pays "Autre".
     */
    isParcoursGuide() {
      return this.isChoixInitial || this.forceChoix;
    },
    isPaysAutre() {
      return !this.forceChoix && this.cataloguePublicPays === PAYS_AUTRE;
    },
    /**
     * Pays "Autre" : aucun catalogue à afficher, la modale ne peut pas être fermée.
     */
    isChoixObligatoire() {
      return this.cataloguePublicPays === PAYS_AUTRE;
    },
    /**
     * Ouverture automatique sur les pages du catalogue public tant que le visiteur n'a pas de catalogue.
     * La page de consultation d'une demande reste accessible depuis le lien reçu par e-mail.
     */
    doitOuvrirAutomatiquement() {
      return this.isChoixPaysCataloguePublicRequis
        && Boolean(this.$route.meta?.cataloguePublic)
        && this.$route.name !== "demande_devis";
    },
    titre() {
      if (this.isPaysAutre) {
        return this.$t("catalogue-public.pays-autre-titre");
      }
      if (this.isParcoursGuide) {
        return this.$t("catalogue-public.choix-pays-titre");
      }
      return this.$t("catalogue-public.changer-de-pays");
    },
    sousTitre() {
      if (this.isPaysAutre) {
        return this.$t("catalogue-public.pays-autre-contenu");
      }
      if (this.isParcoursGuide) {
        return this.$t(`catalogue-public.question-${this.etape}`);
      }
      return this.$t("catalogue-public.modal-changer-pays-description");
    },
    isPaysModifie() {
      return Boolean(this.selectedPays) && this.selectedPays !== this.cataloguePublicPays;
    },
    linesInPanier() {
      return this.panier?.quantite_ligne || 0;
    },
  },
  watch: {
    doitOuvrirAutomatiquement(value) {
      if (value) {
        this.ouvrir();
      }
    },
  },
  mounted() {
    // Tant que le choix est obligatoire, la touche Échap ne ferme pas la modale
    this.onKeydown = (event) => {
      if (event.key === "Escape" && this.isChoixObligatoire) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    this.onCancel = (event) => {
      if (this.isChoixObligatoire) {
        event.preventDefault();
      }
    };
    this.$el.addEventListener("keydown", this.onKeydown, true);
    this.$el.addEventListener("cancel", this.onCancel);

    if (this.$route.query?.[QUERY_CREATION_COMPTE]) {
      // Retour sur le catalogue du pays choisi : on enchaîne sur la demande de compte
      const query = { ...this.$route.query };
      delete query[QUERY_CREATION_COMPTE];
      this.$router.replace({ query });
      this.$nextTick(() => this.ouvrirDemandeCompte());
    } else if (this.doitOuvrirAutomatiquement) {
      this.$nextTick(() => this.ouvrir());
    }
  },
  beforeUnmount() {
    this.$el.removeEventListener("keydown", this.onKeydown, true);
    this.$el.removeEventListener("cancel", this.onCancel);
  },
  methods: {
    ouvrir() {
      if (!this.$el.open) {
        this.$modal.show(this.modalId);
      }
    },
    /**
     * Ouvre la modale de connexion directement sur la demande de compte (choix du profil).
     */
    ouvrirDemandeCompte() {
      window.dispatchEvent(new CustomEvent("modal-connexion:open", {
        detail: { ongletInitial: ONGLET_CHOIX_PROFIL },
      }));
    },
    /**
     * Fermeture (Échap) avant la fin du parcours : on enregistre le pays déjà indiqué,
     * ou la France dont le catalogue est déjà affiché.
     */
    onHidden() {
      // "hidden" suit aussi les fermetures par code : un enregistrement déjà lancé ne doit pas être doublé
      if (!this.isChoixInitial || this.isLoading) {
        return;
      }

      this.enregistrerPays(this.paysEnCours || PAYS_FRANCE);
    },
    reset() {
      this.selectedPays = this.cataloguePublicPays === PAYS_AUTRE ? null : this.cataloguePublicPays;
      this.etape = "france";
      this.paysEnCours = null;
      this.forceChoix = false;
      this.isLoading = false;
    },
    repondreFrance(isFrance) {
      if (isFrance) {
        this.paysEnCours = PAYS_FRANCE;
        this.etape = "besoin";
        return;
      }

      this.selectedPays = null;
      this.etape = "pays";
    },
    /**
     * Pays hors France choisi : "Autre" n'a pas de catalogue (message de contact), sinon on passe au besoin.
     */
    async validerPaysExport() {
      if (!this.selectedPays) {
        return;
      }

      if (this.selectedPays === PAYS_AUTRE) {
        this.isLoading = true;
        try {
          await this.$store.dispatch("setPaysCataloguePublic", PAYS_AUTRE);
          this.forceChoix = false;
          this.etape = "france";
        } catch (error) {
          console.error(error);
          this.$toast.error({ title: this.$t("catalogue-public.choix-pays-erreur") });
        } finally {
          this.isLoading = false;
        }
        return;
      }

      this.paysEnCours = this.selectedPays;
      this.etape = "besoin";
    },
    retour() {
      if (this.etape === "besoin") {
        this.etape = this.paysEnCours === PAYS_FRANCE ? "france" : "pays";
        return;
      }
      if (this.etape === "pays") {
        this.etape = "france";
        return;
      }
      // Étape 1 relancée depuis le message du pays "Autre" : on y revient
      this.forceChoix = false;
    },
    /**
     * Fin du parcours guidé : enregistre le pays puis ouvre la demande de compte ou laisse naviguer (devis).
     */
    async terminerParcours(besoin) {
      if (this.isLoading) {
        return;
      }

      const isDemandeCompte = besoin === BESOIN_COMPTE;
      // Fermeture avant l'enregistrement : la modale ne bascule pas sur l'affichage « Changer de pays »
      this.$modal.hide(this.modalId);
      const isRechargement = await this.enregistrerPays(this.paysEnCours || PAYS_FRANCE, { isDemandeCompte });
      this.forceChoix = false;
      if (!isRechargement && isDemandeCompte) {
        this.ouvrirDemandeCompte();
      }
    },
    /**
     * Le visiteur a déjà un compte : redirection vers la connexion (le pays n'a pas d'utilité pour l'espace connecté).
     */
    seConnecter() {
      this.$store.commit("setIsRedirecting", true);
      window.location.href = config.loginUrl;
    },
    changerDePays() {
      this.selectedPays = null;
      this.etape = "france";
      this.paysEnCours = null;
      this.forceChoix = true;
    },
    contacter() {
      window.location.href = getMailtoPaysAutre(this.$t, config.mails.publicContact);
    },
    /**
     * Changement de pays depuis le header.
     */
    async validerPays() {
      if (!this.selectedPays || this.isLoading) {
        return;
      }

      if (!this.isPaysModifie) {
        // Pays inchangé : rien à enregistrer
        this.$modal.hide(this.modalId);
        return;
      }

      const isRechargement = await this.enregistrerPays(this.selectedPays);
      if (!isRechargement && this.cataloguePublicPays !== PAYS_AUTRE) {
        this.$modal.hide(this.modalId);
      }
    },
    /**
     * Enregistre le pays. Si son catalogue diffère de celui affiché, recharge la page sur ce catalogue
     * (pour que toutes les données affichées correspondent) et renvoie true.
     * @param {String} pays Code du pays.
     * @param {Object} options isDemandeCompte : ouvrir la demande de compte après le rechargement.
     * @returns {Promise<Boolean>} true si la page est rechargée.
     */
    async enregistrerPays(pays, { isDemandeCompte = false } = {}) {
      if (pays === this.cataloguePublicPays) {
        return false;
      }

      const ancienSlug = this.cataloguePublicSlug;
      this.isLoading = true;
      try {
        const data = await this.$store.dispatch("setPaysCataloguePublic", pays);
        const slug = data?.catalogue_public?.slug;

        if (pays === PAYS_AUTRE || (slug && slug === ancienSlug)) {
          // "Autre" : la modale affiche le message de contact. Même catalogue : rien à recharger.
          this.isLoading = false;
          return false;
        }

        // Sans catalogue configuré pour le pays, on repart de l'accueil
        const target = slug
          ? this.$router.resolve({
            name: "catalogues_ressources_numeriques",
            params: { catalogueSlug: slug },
            query: isDemandeCompte ? { [QUERY_CREATION_COMPTE]: "1" } : {},
          })
          : this.$router.resolve({
            name: "home",
            query: isDemandeCompte ? { [QUERY_CREATION_COMPTE]: "1" } : {},
          });
        window.location.assign(target.href);
        return true;
      } catch (error) {
        console.error(error);
        this.$toast.error({ title: this.$t("catalogue-public.choix-pays-erreur") });
        this.isLoading = false;
        return false;
      }
    },
  },
};
</script>

<style lang="scss">
@use "@lde/core_lde_vue/dist/assets/styles/_variables.scss" as var;

#modal_choix_pays_catalogue_public {
  // La liste déroulante du pays doit pouvoir dépasser de la modale
  overflow: visible;

  .modal-body { overflow: unset; }

  .modal-footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: var.$space-x-tiny;
  }

  .modal-choix-pays-content {
    display: flex;
    flex-direction: column;
    gap: var.$space-small;
  }

  .choix-parcours {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: var.$space-small;

    // Compte, devis ou connexion : boutons presque carrés sur une seule ligne, empilés sur petit écran
    &--besoin {
      grid-template-columns: none;
      grid-auto-flow: column;
      grid-auto-columns: 18rem;
      justify-content: center;

      .choix-parcours-carte {
        min-height: 14rem;
        padding: var.$space-tiny var.$space-x-tiny;
      }

      @media screen and (max-width: 800px) {
        grid-auto-flow: row;
        grid-auto-columns: auto;
        justify-content: stretch;
      }
    }
  }

  .choix-parcours-carte {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var.$space-xx-tiny;
    min-height: 8rem;
    padding: var.$space-tiny;
    border: 1px solid rgba(var.$blue, 0.35);
    border-radius: var.$border-radius-x-tiny;
    background: linear-gradient(180deg, rgba(var.$blue, 0.06), rgba(var.$blue, 0.015));
    color: var.$blue;
    text-align: center;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      border-color: var.$blue;
      box-shadow: var.$shadow-4;
    }

    &:focus-visible {
      outline: 2px solid var.$blue;
      outline-offset: 2px;
    }

    &:disabled { cursor: wait; }
  }
}
</style>
